import { makeAutoObservable, runInAction } from "mobx";
import { esgService } from "../../services";
import { esgReportBuilderStore } from "./esg-report-builder.store";
import { esgDataEntryStore } from "./esg-data-entry.store";
import { showToast } from "@/lib/utils/toast";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";

class ESGCollaborationStore {
  // Delegation
  delegatedDisclosures = new Map(); // code -> { email, message, status }
  delegatedEntries = new Map(); // code -> entryId -> { email, message }

  // Sharing
  sharedWith = [];

  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed
  get totalDelegations() {
    return this.delegatedDisclosures.size;
  }

  get pendingDelegations() {
    let count = 0;
    this.delegatedDisclosures.forEach((delegation) => {
      if (delegation.status === "pending") count++;
    });
    return count;
  }

  isDisclosureDelegated = (code) => {
    return this.delegatedDisclosures.has(code);
  };

  getDisclosureDelegation = (code) => {
    return this.delegatedDisclosures.get(code);
  };

  isEntryDelegated = (code, entryId) => {
    return this.delegatedEntries.get(code)?.has(entryId) || false;
  };

  getEntryDelegation = (code, entryId) => {
    return this.delegatedEntries.get(code)?.get(entryId);
  };

  // Delegation actions
  delegateDisclosure = (code, email, message) => {
    this.delegatedDisclosures.set(code, {
      email,
      message,
      status: "pending",
      delegatedAt: new Date(),
    });
  };

  undelegateDisclosure = (code) => {
    this.delegatedDisclosures.delete(code);
  };

  delegateEntry = (code, entryId, email, message) => {
    if (!this.delegatedEntries.has(code)) {
      this.delegatedEntries.set(code, new Map());
    }

    this.delegatedEntries.get(code).set(entryId, {
      email,
      message,
      status: "pending",
      delegatedAt: new Date(),
    });
  };

  undelegateEntry = (code, entryId) => {
    const entries = this.delegatedEntries.get(code);
    if (entries) {
      entries.delete(entryId);
      if (entries.size === 0) {
        this.delegatedEntries.delete(code);
      }
    }
  };

  updateDelegationStatus = (code, status) => {
    const delegation = this.delegatedDisclosures.get(code);
    if (delegation) {
      delegation.status = status;
    }
  };

  // Sharing
  shareReport = (email, role, permissions) => {
    this.sharedWith.push({
      email,
      role,
      permissions,
      sharedAt: new Date(),
    });
  };

  unshareReport = (email) => {
    this.sharedWith = this.sharedWith.filter((s) => s.email !== email);
  };

  sendDelegations = async (codes, values) => {
    const { reportId, isNew, createReport } = esgReportBuilderStore;

    // Auto-save if new report
    if (isNew) {
      const saved = await createReport();
      if (!saved) {
        showToast.error("Failed to save report");
        return;
      }
    }

    runInAction(() => {
      this.loading = true;
    });

    try {
      const delegations = codes.map((code) => ({
        disclosureCode: code,
        email: values.email,
        message: values.message,
      }));

      const delegationPromise = esgService.sendDelegations(
        esgReportBuilderStore.reportId, // Use current reportId (might be updated after save)
        delegations,
      );

      showToast.promise(delegationPromise, {
        loading: "Sending delegations",
        success: "Successfully sent",
        error: "Failed to send delegation",
      });

      const data = await promiseWithMinDelay(delegationPromise);

      runInAction(() => {
        data.forEach((d) => {
          this.delegateDisclosure(d.disclosureCode, d.delegatedTo, d.message);
        });
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  sendEntryDelegation = async (code, entryId, values) => {
    const { isNew, createReport } = esgReportBuilderStore;

    // Auto-save if new report
    if (isNew) {
      const saved = await createReport();
      if (!saved) {
        showToast.error("Failed to save report");
        return;
      }
    }

    runInAction(() => {
      this.loading = true;
    });

    try {
      const payload = {
        disclosureCode: code,
        entryId,
        email: values.email,
        message: values.message,
      };

      const delegationPromise = esgService.sendDelegation(
        esgReportBuilderStore.reportId, // Use current reportId
        payload,
      );

      showToast.promise(delegationPromise, {
        loading: "Sending delegation",
        success: "Successfully delegated entry",
        error: "Failed to delegate entry",
      });

      await promiseWithMinDelay(delegationPromise);

      runInAction(() => {
        // Track in collaboration store
        this.delegateEntry(code, entryId, values.email, values.message);

        // Add delegatedTo to actual entry object and clear data
        const entries = esgDataEntryStore.entriesByCode.get(code);
        if (entries) {
          const entry = entries.find((e) => e.entryId === entryId);
          if (entry) {
            // Add delegation metadata
            entry.delegatedTo = {
              email: values.email,
              message: values.message,
              delegatedAt: new Date(),
            };

            // Clear entry data (user is delegating - they don't have the data)
            entry.value = "";
            entry.unit = "";
            entry.valueUnit = "";
            entry.location = "";
            entry.category = "";
            entry.denominator = null;
            entry.subEntries = [];
            entry.proofs = [];
          }
        }

        // Mark as dirty and trigger auto-save to persist delegatedTo to backend
        esgDataEntryStore.isDirty = true;
        esgDataEntryStore.queueAutoSave();
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  removeEntryDelegation = (code, entryId) => {
    // Remove from collaboration store
    this.undelegateEntry(code, entryId);

    // Remove delegatedTo from entry object
    const entries = esgDataEntryStore.entriesByCode.get(code);
    if (entries) {
      const entry = entries.find((e) => e.entryId === entryId);
      if (entry) {
        delete entry.delegatedTo;
      }
    }

    // Auto-save will persist the removal to backend
    esgDataEntryStore.isDirty = true;
    esgDataEntryStore.queueAutoSave();
  };

  loadData = (data) => {
    runInAction(() => {
      this.delegatedDisclosures.clear();
      this.delegatedEntries.clear();
      this.sharedWith = data.sharedWith || [];

      data.delegatedDisclosures?.forEach((d) => {
        this.delegatedDisclosures.set(d.disclosureCode, {
          email: d.email,
          message: d.message,
          status: d.status || "pending",
          delegatedAt: d.delegatedAt,
        });
      });

      // Load entry-level delegations from disclosure data
      data.disclosureData?.forEach((item) => {
        item.entries?.forEach((entry) => {
          if (entry.delegatedTo?.email) {
            this.delegateEntry(
              item.disclosureCode,
              entry.entryId,
              entry.delegatedTo.email,
              entry.delegatedTo.message || "",
            );
          }
        });
      });
    });
  };

  resetCollaboration = () => {
    runInAction(() => {
      this.delegatedDisclosures.clear();
      this.delegatedEntries.clear();
      this.sharedWith = [];
    });
  };
}

export const esgCollaborationStore = new ESGCollaborationStore();
